import { Geolocation } from '@capacitor/geolocation';

import { isNativePlatform } from './platform';

export interface Coords {
    latitude: number,
    longitude: number,
}

export type GeolocationError = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

export interface GeolocationResult {
    coords?: Coords,
    error?: GeolocationError,
}

const DEFAULT_TIMEOUT_MS = 10_000;

export async function getCurrentPosition(timeoutMs = DEFAULT_TIMEOUT_MS): Promise<GeolocationResult> {
    if (isNativePlatform()) {
        return getNativePosition(timeoutMs);
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
        return { error: 'unsupported' };
    }
    return new Promise<GeolocationResult>((resolve) => {
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ coords: { latitude: pos.coords.latitude, longitude: pos.coords.longitude } }),
            (err) => {
                if (err.code === err.PERMISSION_DENIED) resolve({ error: 'denied' });
                else if (err.code === err.TIMEOUT) resolve({ error: 'timeout' });
                else resolve({ error: 'unavailable' });
            },
            { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
        );
    });
}

// Native path via the Capacitor Geolocation plugin. Requests OS location
// permission on demand and maps plugin errors onto our GeolocationError union.
async function getNativePosition(timeoutMs: number): Promise<GeolocationResult> {
    try {
        const perm = await Geolocation.checkPermissions();
        if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
            const req = await Geolocation.requestPermissions();
            if (req.location !== 'granted' && req.coarseLocation !== 'granted') {
                return { error: 'denied' };
            }
        }
        const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: timeoutMs,
            maximumAge: 30_000,
        });
        return { coords: { latitude: pos.coords.latitude, longitude: pos.coords.longitude } };
    } catch (e) {
        const msg = String((e as { message?: string })?.message ?? e ?? '').toLowerCase();
        if (msg.includes('denied') || msg.includes('permission')) return { error: 'denied' };
        if (msg.includes('timeout')) return { error: 'timeout' };
        return { error: 'unavailable' };
    }
}

// haversineMeters returns the great-circle distance in meters between two
// (latitude, longitude) pairs in decimal degrees.
export function haversineMeters(a: Coords, b: Coords): number {
    const earthRadiusM = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dPhi = toRad(b.latitude - a.latitude);
    const dLambda = toRad(b.longitude - a.longitude);
    const phi1 = toRad(a.latitude);
    const phi2 = toRad(b.latitude);
    const h = Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
    return earthRadiusM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}
